"use client"

import { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from "@mui/material"
import { useTheme, alpha } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PersonIcon from "@mui/icons-material/Person"
import InventoryIcon from "@mui/icons-material/Inventory"
import SellIcon from "@mui/icons-material/Sell"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff"
import GroupsIcon from "@mui/icons-material/Groups"
import HotelIcon from "@mui/icons-material/Hotel"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  Area,
  AreaChart,
  ComposedChart,
} from "recharts"

const COLORS = [
  "#3B82F6", // Blue - Color primario para elementos principales
  "#10B981", // Emerald - Para métricas positivas y crecimiento
  "#F59E0B", // Amber - Para alertas y métricas de atención
  "#EF4444", // Red - Para métricas críticas o negativas
  "#8B5CF6", // Violet - Para diferenciación de categorías
  "#06B6D4", // Cyan - Para datos secundarios
  "#84CC16", // Lime - Para métricas de rendimiento
  "#F97316", // Orange - Para destacar elementos importantes
  "#EC4899", // Pink - Para categorías especiales
  "#6B7280", // Gray - Para elementos neutros
]

/**
 * FUNCIÓN UTILITARIA: formatCurrency
 *
 * Propósito:
 * - Formatea números como moneda argentina (ARS)
 * - Elimina decimales para mejor legibilidad en dashboards
 * - Utiliza separadores de miles localizados
 *
 * Configuración:
 * - Locale: es-AR (español argentino)
 * - Currency: ARS (peso argentino)
 * - MaximumFractionDigits: 0 (sin decimales)
 */
const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * COMPONENTE METRICCARD
 *
 * Propósito: Renderiza tarjetas de métricas con diseño profesional y efectos hover
 *
 * Funcionamiento:
 * 1. Recibe props: title (título), value (valor principal), subtitle (texto secundario),
 *    icon (ícono), color (tema de color), trend (tendencia opcional)
 * 2. Aplica gradiente de fondo basado en el color del tema
 * 3. Muestra ícono en avatar circular con colores temáticos
 * 4. Si hay trend, muestra indicador de tendencia con ícono y porcentaje
 * 5. Aplica efectos de hover (elevación y sombra)
 */
const MetricCard = ({ title, value, subtitle, icon: Icon, color = "primary", trend }) => {
  const theme = useTheme()

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        // Gradiente sutil basado en el color del tema
        background: `linear-gradient(135deg, ${theme.palette[color].main}08 0%, ${theme.palette[color].main}15 100%)`,
        border: `1px solid ${theme.palette[color].main}20`,
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette[color].main, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${theme.palette[color].main}15`,
              color: theme.palette[color].main,
              width: 48,
              height: 48,
            }}
          >
            <Icon />
          </Avatar>
        </Box>
        {/* Indicador de tendencia opcional */}
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDownIcon sx={{ color: "error.main", fontSize: 16, mr: 0.5 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: trend > 0 ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {Math.abs(trend)}% vs período anterior
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * COMPONENTE TABPANEL
 *
 * Propósito: Maneja la visualización condicional del contenido de cada pestaña
 *
 * Funcionamiento:
 * 1. Recibe props: children (contenido), value (pestaña actual), index (índice de la pestaña)
 * 2. Solo renderiza el contenido cuando value === index
 * 3. Aplica atributos de accesibilidad para navegación por teclado
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

/**
 * FUNCIÓN A11YPROPS
 *
 * Propósito: Genera propiedades de accesibilidad para las pestañas
 *
 * Funcionamiento:
 * 1. Recibe el índice de la pestaña
 * 2. Retorna objeto con id y aria-controls para navegación accesible
 */
function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  }
}

/**
 * COMPONENTE PRINCIPAL: GRAFICASDETALLESVENTASMODAL
 *
 * Propósito: Modal de análisis detallado de ventas por items individuales
 *
 * Props:
 * - open: boolean - Controla la visibilidad del modal
 * - handleClose: function - Función para cerrar el modal
 * - data: array - Datos de detalles de ventas (items individuales de transacciones)
 * - loading: boolean - Estado de carga de datos
 *
 * Estructura de datos esperada:
 * - Cada item contiene: montoTotal, cantidad, item (descripción, código),
 *   Transaccion (id, fecha, entidad, usuario, transaccionPago)
 */
const GraficasDetallesVentasModal = ({ open, handleClose, data, loading = false }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  /**
   * CÁLCULO DE MÉTRICAS GENERALES - AGENCIA DE VIAJES
   *
   * Propósito: Procesa los datos para obtener KPIs principales de viajes
   *
   * Funcionamiento:
   * 1. Suma todos los montos totales para obtener ventas totales
   * 2. Cuenta pasajeros únicos (entidades/clientes)
   * 3. Cuenta destinos únicos visitados
   * 4. Cuenta grupos/paquetes únicos vendidos
   * 5. Calcula viajes realizados (cerrados) vs pendientes
   * 6. Calcula promedio de noches por viaje
   * 7. Calcula ticket promedio por pasajero
   *
   * Retorna: Objeto con todas las métricas calculadas
   */
  const metricsGenerales = useMemo(() => {
    if (!data || data.length === 0) return {}

    const totalVentas = data.reduce((sum, item) => sum + (item.montoTotal || 0), 0)
    const pasajerosUnicos = new Set(data.map((item) => item.Transaccion?.entidad?.id)).size
    const destinosUnicos = new Set(
      data.map((item) => item.destinoViaje).filter(Boolean)
    ).size
    const gruposUnicos = new Set(data.map((item) => item.idItem)).size
    const viajesRealizados = data.filter(item => item.estadoViaje === 'cerrado').length
    const viajesPendientes = data.filter(item => item.estadoViaje === 'activo').length
    const nochesTotales = data.reduce((sum, item) => sum + (item.nochesViaje || 0), 0)
    const promedioNoches = data.length > 0 ? nochesTotales / data.length : 0
    const ticketPromedio = pasajerosUnicos > 0 ? totalVentas / pasajerosUnicos : 0

    return {
      totalVentas,
      pasajerosUnicos,
      destinosUnicos,
      gruposUnicos,
      viajesRealizados,
      viajesPendientes,
      promedioNoches,
      ticketPromedio,
    }
  }, [data])

  /**
   * FUNCIÓN: PROCESARTOPDESTINOS
   *
   * Propósito: Analiza y rankea los destinos más vendidos
   *
   * Funcionamiento:
   * 1. Crea objeto destinoData para agrupar datos por destino
   * 2. Itera sobre cada registro de venta:
   *    - Identifica destino del viaje
   *    - Acumula cantidad de pasajeros y monto total
   *    - Registra grupos vendidos únicos
   *    - Suma noches totales para calcular promedio
   * 3. Calcula métricas adicionales:
   *    - Número de grupos diferentes que visitaron el destino
   *    - Promedio de noches por pasajero en ese destino
   * 4. Ordena por cantidad de pasajeros (descendente)
   * 5. Retorna top 10 destinos con todas las métricas
   *
   * Retorna: Array de objetos con métricas por destino
   */
  const procesarTopDestinos = () => {
    const destinoData = {}

    data.forEach((item) => {
      const destinoNombre = item.destinoViaje || "Sin destino"
      
      if (!destinoData[destinoNombre]) {
        destinoData[destinoNombre] = {
          nombre: destinoNombre,
          cantidadPasajeros: 0,
          montoTotal: 0,
          gruposVendidos: new Set(),
          nochesPromedio: 0,
          totalNoches: 0,
        }
      }

      destinoData[destinoNombre].cantidadPasajeros += 1
      destinoData[destinoNombre].montoTotal += item.montoTotal || 0
      destinoData[destinoNombre].gruposVendidos.add(item.item?.descripcion || item.item?.codigo)
      destinoData[destinoNombre].totalNoches += item.nochesViaje || 0
    })

    return Object.values(destinoData)
      .map((destino) => ({
        ...destino,
        gruposVendidos: destino.gruposVendidos.size,
        nochesPromedio: destino.cantidadPasajeros > 0 
          ? (destino.totalNoches / destino.cantidadPasajeros).toFixed(1)
          : 0,
      }))
      .sort((a, b) => b.cantidadPasajeros - a.cantidadPasajeros)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARTEMPORADAS
   *
   * Propósito: Analiza tendencias de viajes por temporadas/meses
   *
   * Funcionamiento:
   * 1. Agrupa ventas por mes de viaje (no de venta)
   * 2. Calcula cantidad de viajes, pasajeros y monto por mes
   * 3. Cuenta destinos únicos visitados en cada mes
   * 4. Ordena cronológicamente
   *
   * Retorna: Array de objetos con métricas por mes
   */
  const procesarTemporadas = () => {
    const viajesPorMes = {}

    data.forEach((item) => {
      if (!item.fechaInicioViaje) return
      
      const fechaViaje = new Date(item.fechaInicioViaje)
      const mesAnio = `${fechaViaje.toLocaleString('es-AR', { month: 'short' })} ${fechaViaje.getFullYear()}`
      
      if (!viajesPorMes[mesAnio]) {
        viajesPorMes[mesAnio] = {
          mes: mesAnio,
          cantidadViajes: 0,
          pasajeros: 0,
          montoTotal: 0,
          destinosUnicos: new Set(),
          fechaOrden: fechaViaje,
        }
      }

      viajesPorMes[mesAnio].cantidadViajes += 1
      viajesPorMes[mesAnio].pasajeros += 1
      viajesPorMes[mesAnio].montoTotal += item.montoTotal || 0
      viajesPorMes[mesAnio].destinosUnicos.add(item.destinoViaje)
    })

    return Object.values(viajesPorMes)
      .map((mes) => ({
        ...mes,
        destinosUnicos: mes.destinosUnicos.size,
      }))
      .sort((a, b) => a.fechaOrden - b.fechaOrden)
  }

  /**
   * FUNCIÓN: PROCESARTOPGRUPOS
   *
   * Propósito: Analiza y rankea los grupos/paquetes más vendidos
   *
   * Funcionamiento:
   * 1. Agrupa por grupo (item/paquete)
   * 2. Cuenta pasajeros, monto total y destinos visitados
   * 3. Registra fechas de viaje únicas
   * 4. Ordena por cantidad de pasajeros
   *
   * Retorna: Array de objetos con métricas por grupo
   */
  const procesarTopGrupos = () => {
    const grupoData = {}

    data.forEach((item) => {
      const grupoKey = `${item.item?.codigo || ""} - ${item.item?.descripcion || "Sin descripción"}`
      
      if (!grupoData[grupoKey]) {
        grupoData[grupoKey] = {
          nombre: grupoKey,
          cantidadPasajeros: 0,
          montoTotal: 0,
          destinosVisitados: new Set(),
          fechasViaje: new Set(),
        }
      }

      grupoData[grupoKey].cantidadPasajeros += 1
      grupoData[grupoKey].montoTotal += item.montoTotal || 0
      grupoData[grupoKey].destinosVisitados.add(item.destinoViaje)
      if (item.fechaInicioViaje) {
        grupoData[grupoKey].fechasViaje.add(item.fechaInicioViaje)
      }
    })

    return Object.values(grupoData)
      .map((grupo) => ({
        ...grupo,
        destinosVisitados: grupo.destinosVisitados.size,
        viajesRealizados: grupo.fechasViaje.size,
      }))
      .sort((a, b) => b.cantidadPasajeros - a.cantidadPasajeros)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARANALISIS PAGOS
   *
   * Propósito: Analiza el estado de pagos y cobranzas
   *
   * Funcionamiento:
   * 1. Calcula totales de pagado y saldo pendiente
   * 2. Cuenta pasajeros sin deuda vs con deuda
   * 3. Calcula porcentaje de cobertura de pagos
   *
   * Retorna: Objeto con métricas financieras
   */
  const procesarAnalisisPagos = () => {
    const estadoPagos = {
      totalPagado: 0,
      totalSaldo: 0,
      pasajerosSinDeuda: 0,
      pasajerosConDeuda: 0,
      promedioCobertura: 0,
    }

    data.forEach((item) => {
      estadoPagos.totalPagado += item.pagado || 0
      estadoPagos.totalSaldo += item.saldo || 0
      
      if ((item.saldo || 0) === 0) {
        estadoPagos.pasajerosSinDeuda += 1
      } else {
        estadoPagos.pasajerosConDeuda += 1
      }
    })

    const totalVentas = estadoPagos.totalPagado + estadoPagos.totalSaldo
    estadoPagos.promedioCobertura = totalVentas > 0 
      ? ((estadoPagos.totalPagado / totalVentas) * 100).toFixed(1)
      : 0

    return estadoPagos
  }

  /**
   * FUNCIÓN: PROCESARTOP10CLIENTESPORFECHA
   *
   * Propósito: Analiza comportamiento y ranking de clientes por volumen de compras
   *
   * Funcionamiento:
   * 1. Crea objeto clienteData para agrupar por cliente
   * 2. Para cada registro de venta:
   *    - Identifica cliente por nombre + apellido de la entidad
   *    - Convierte fecha a formato local (es-AR)
   *    - Acumula ventas totales y cantidad de items
   *    - Registra fechas únicas y transacciones únicas en Sets
   *    - Actualiza fecha de última compra
   * 3. Calcula métricas avanzadas:
   *    - Días activos: número de fechas únicas de compra
   *    - Cantidad de compras: número de transacciones únicas
   *    - Ticket promedio: ventas totales / cantidad de compras
   *    - Items por compra: total items / cantidad de compras
   * 4. Ordena por total de ventas (descendente)
   * 5. Retorna top 10 clientes con análisis completo
   *
   * Retorna: Array de objetos con métricas detalladas por cliente
   */
  const procesarTop10ClientesPorFecha = () => {
    const clienteData = {}

    data.forEach((item) => {
      const clienteNombre =
        `${item.Transaccion?.entidad?.descripcion || "Sin nombre"} ${item.Transaccion?.entidad?.apellido || ""}`.trim()
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!clienteData[clienteNombre]) {
        clienteData[clienteNombre] = {
          nombre: clienteNombre,
          totalVentas: 0,
          cantidadCompras: 0,
          cantidadItems: 0,
          ultimaCompra: fecha,
          fechas: new Set(),
          transacciones: new Set(),
        }
      }

      clienteData[clienteNombre].totalVentas += item.montoTotal || 0
      clienteData[clienteNombre].cantidadItems += item.cantidad || 0
      clienteData[clienteNombre].fechas.add(fecha)
      clienteData[clienteNombre].transacciones.add(item.Transaccion?.id)

      // Actualizar última compra comparando fechas
      if (
        new Date(fecha.split("/").reverse().join("-")) >
        new Date(clienteData[clienteNombre].ultimaCompra.split("/").reverse().join("-"))
      ) {
        clienteData[clienteNombre].ultimaCompra = fecha
      }
    })

    return Object.values(clienteData)
      .map((cliente) => ({
        ...cliente,
        diasActivos: cliente.fechas.size,
        cantidadCompras: cliente.transacciones.size,
        ticketPromedio: cliente.cantidadCompras > 0 ? cliente.totalVentas / cliente.cantidadCompras : 0,
        itemsPorCompra: cliente.cantidadCompras > 0 ? cliente.cantidadItems / cliente.cantidadCompras : 0,
        fechas: undefined,
        transacciones: undefined,
      }))
      .sort((a, b) => b.totalVentas - a.totalVentas)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARMEDIOSDEPAGO
   *
   * Propósito: Analiza distribución y uso de medios de pago en las ventas
   *
   * Funcionamiento:
   * 1. Crea objetos mediosData y tiposData para doble agrupación
   * 2. Para cada item de venta:
   *    - Accede a transaccionPago de la transacción
   *    - Para cada pago asociado:
   *      a) Identifica medio de pago específico y su tipo
   *      b) Calcula monto proporcional del item en ese pago:
   *         monto_pago * cotización * (monto_item / monto_total_transacción)
   *      c) Agrupa por medio específico (ej: "Visa", "Mastercard")
   *      d) Agrupa por tipo de medio (ej: "Tarjeta de Crédito", "Efectivo")
   *      e) Registra transacciones únicas para cada medio/tipo
   * 3. Procesa datos finales:
   *    - Convierte Sets de transacciones a contadores
   *    - Ordena medios por cantidad de transacciones
   *    - Ordena tipos por monto total
   * 4. Retorna objeto con ambos análisis: medios específicos y tipos
   *
   * Retorna: Objeto con arrays 'medios' y 'tipos', cada uno con métricas completas
   */
  const procesarMediosDePago = () => {
    const mediosData = {}
    const tiposData = {}

    data.forEach((item) => {
      const transaccionPagos = item.Transaccion?.transaccionPago || []

      transaccionPagos.forEach((tp) => {
        const pago = tp.pago
        if (pago?.medioDePago) {
          const medioNombre = pago.medioDePago.descripcion
          const tipoMedio = pago.medioDePago.tipoMedioDePago?.descripcion || "Sin tipo"
          // Cálculo proporcional del monto del item en este medio de pago
          const montoItem = pago.montoTotal * (pago.cotizacion || 1) * (item.montoTotal / item.Transaccion.montoTotal)

          // Agrupación por medio específico
          if (!mediosData[medioNombre]) {
            mediosData[medioNombre] = {
              nombre: medioNombre,
              tipo: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          mediosData[medioNombre].transacciones.add(item.Transaccion?.id)
          mediosData[medioNombre].montoTotal += montoItem

          // Agrupación por tipo de medio
          if (!tiposData[tipoMedio]) {
            tiposData[tipoMedio] = {
              nombre: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          tiposData[tipoMedio].transacciones.add(item.Transaccion?.id)
          tiposData[tipoMedio].montoTotal += montoItem
        }
      })
    })

    // Procesamiento final de datos
    const mediosFinales = Object.values(mediosData)
      .map((medio) => ({
        ...medio,
        cantidadTransacciones: medio.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.cantidadTransacciones - a.cantidadTransacciones)

    const tiposFinales = Object.values(tiposData)
      .map((tipo) => ({
        ...tipo,
        cantidadTransacciones: tipo.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.montoTotal - a.montoTotal)

    return { medios: mediosFinales, tipos: tiposFinales }
  }

  /**
   * FUNCIÓN: PROCESARTOPVENDEDORES
   *
   * Propósito: Analiza performance y métricas de productividad por vendedor
   *
   * Funcionamiento:
   * 1. Crea objeto vendedorData para agrupar por usuario vendedor
   * 2. Para cada item vendido:
   *    - Identifica vendedor por usuario de la transacción
   *    - Acumula ventas totales y cantidad de items
   *    - Registra en Sets: transacciones únicas, clientes únicos, items únicos
   * 3. Calcula métricas de performance:
   *    - Cantidad de transacciones realizadas
   *    - Número de clientes únicos atendidos
   *    - Variedad de items vendidos (diversificación)
   *    - Ticket promedio: ventas totales / transacciones
   *    - Items por transacción: productividad por venta
   * 4. Ordena por total de ventas (descendente)
   * 5. Retorna análisis completo de todos los vendedores
   *
   * Retorna: Array de objetos con métricas de performance por vendedor
   */
  const procesarTopVendedores = () => {
    const vendedorData = {}

    data.forEach((item) => {
      const vendedorNombre = item.Transaccion?.usuario?.usuario || "Sin vendedor"

      if (!vendedorData[vendedorNombre]) {
        vendedorData[vendedorNombre] = {
          nombre: vendedorNombre,
          totalVentas: 0,
          cantidadItems: 0,
          transacciones: new Set(),
          clientes: new Set(),
          itemsUnicos: new Set(),
        }
      }

      vendedorData[vendedorNombre].totalVentas += item.montoTotal || 0
      vendedorData[vendedorNombre].cantidadItems += item.cantidad || 0
      vendedorData[vendedorNombre].transacciones.add(item.Transaccion?.id)
      vendedorData[vendedorNombre].clientes.add(item.Transaccion?.entidad?.id)
      vendedorData[vendedorNombre].itemsUnicos.add(item.idItem)
    })

    return Object.values(vendedorData)
      .map((vendedor) => ({
        ...vendedor,
        cantidadTransacciones: vendedor.transacciones.size,
        clientesUnicos: vendedor.clientes.size,
        itemsUnicos: vendedor.itemsUnicos.size,
        ticketPromedio: vendedor.cantidadTransacciones > 0 ? vendedor.totalVentas / vendedor.cantidadTransacciones : 0,
        itemsPorTransaccion:
          vendedor.cantidadTransacciones > 0 ? vendedor.cantidadItems / vendedor.cantidadTransacciones : 0,
        transacciones: undefined,
        clientes: undefined,
      }))
      .sort((a, b) => b.totalVentas - a.totalVentas)
  }

  /**
   * FUNCIÓN: PROCESARANALISISTEMPOAL
   *
   * Propósito: Analiza evolución temporal de ventas día por día
   *
   * Funcionamiento:
   * 1. Crea objeto ventasPorDia para agrupar por fecha
   * 2. Para cada item vendido:
   *    - Convierte fecha de transacción a formato local (es-AR)
   *    - Acumula ventas totales y cantidad de items por día
   *    - Registra transacciones únicas por día en Set
   * 3. Calcula métricas diarias:
   *    - Total de ventas del día
   *    - Cantidad de items vendidos
   *    - Número de transacciones realizadas
   *    - Ticket promedio del día: ventas / transacciones
   * 4. Ordena cronológicamente por fecha
   * 5. Retorna serie temporal completa para gráficos de tendencias
   *
   * Retorna: Array ordenado por fecha con métricas diarias
   */
  const procesarAnalisisTemporal = () => {
    const ventasPorDia = {}

    data.forEach((item) => {
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!ventasPorDia[fecha]) {
        ventasPorDia[fecha] = {
          fecha,
          totalVentas: 0,
          cantidadItems: 0,
          transacciones: new Set(),
        }
      }

      ventasPorDia[fecha].totalVentas += item.montoTotal || 0
      ventasPorDia[fecha].cantidadItems += item.cantidad || 0
      ventasPorDia[fecha].transacciones.add(item.Transaccion?.id)
    })

    return Object.values(ventasPorDia)
      .map((dia) => ({
        ...dia,
        cantidadTransacciones: dia.transacciones.size,
        ticketPromedio: dia.cantidadTransacciones > 0 ? dia.totalVentas / dia.cantidadTransacciones : 0,
        transacciones: undefined,
      }))
      .sort(
        (a, b) => new Date(a.fecha.split("/").reverse().join("-")) - new Date(b.fecha.split("/").reverse().join("-")),
      )
  }

  const processChannelData = () => {
    const channelMap = new Map()

    data.forEach((detail) => {
      const canalEntidad = detail.Transaccion?.entidad?.canalEntidad
      if (canalEntidad) {
        const channelId = canalEntidad.id
        const channelName = canalEntidad.descripcion

        if (!channelMap.has(channelId)) {
          channelMap.set(channelId, {
            id: channelId,
            name: channelName,
            totalSales: 0,
            transactionCount: 0,
            totalQuantity: 0,
            uniqueCustomers: new Set(),
          })
        }

        const channel = channelMap.get(channelId)
        channel.totalSales += detail.montoTotal || 0
        channel.transactionCount += 1
        channel.totalQuantity += detail.cantidad || 0

        if (detail.Transaccion?.entidad?.id) {
          channel.uniqueCustomers.add(detail.Transaccion.entidad.id)
        }
      }
    })

    return Array.from(channelMap.values()).map((channel) => ({
      ...channel,
      uniqueCustomers: channel.uniqueCustomers.size,
      averageTicket: channel.transactionCount > 0 ? channel.totalSales / channel.transactionCount : 0,
    }))
  }

  const channelData = processChannelData()
  const hasChannelData = channelData.length > 0

  // Procesamiento de todos los datos usando las funciones definidas - AGENCIA DE VIAJES
  const datosTopDestinos = procesarTopDestinos()
  const datosTemporadas = procesarTemporadas()
  const datosTopGrupos = procesarTopGrupos()
  const datosTop10Clientes = procesarTop10ClientesPorFecha()
  const datosTopVendedores = procesarTopVendedores()
  const datosAnalisisPagos = procesarAnalisisPagos()
  const datosAnalisisTemporal = procesarAnalisisTemporal()
  const { medios: datosMediosPago, tipos: datosTiposPago } = procesarMediosDePago()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      {/* Header del modal con gradiente y branding */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <AnalyticsIcon sx={{ fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Análisis de Rendimiento 
          </Typography>
          {/* <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Análisis detallado de rendimiento y métricas de ventas
          </Typography> */}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", minHeight: 600 }}>
        {loading ? (
          // Estado de carga con spinner y mensajes informativos
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 600,
              gap: 3,
            }}
          >
            <CircularProgress size={80} color="primary" thickness={4} />
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
              Procesando datos de análisis...
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 400 }}>
              Analizando patrones de ventas, comportamiento de clientes y métricas de rendimiento
            </Typography>
          </Box>
        ) : (
          <>
            {/* Panel de resumen ejecutivo con KPIs principales */}
            <Box sx={{ p: 3, bgcolor: "grey.50" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                Resumen Ejecutivo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Ventas Totales"
                    value={formatCurrency(metricsGenerales.totalVentas || 0)}
                    subtitle={`Ticket promedio: ${formatCurrency(metricsGenerales.ticketPromedio || 0)}`}
                    icon={AttachMoneyIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Pasajeros Únicos"
                    value={metricsGenerales.pasajerosUnicos?.toLocaleString("es-AR") || "0"}
                    subtitle="Clientes viajeros"
                    icon={PersonIcon}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Destinos Visitados"
                    value={metricsGenerales.destinosUnicos?.toLocaleString("es-AR") || "0"}
                    subtitle={`${metricsGenerales.gruposUnicos} grupos activos`}
                    icon={LocationOnIcon}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Viajes Realizados"
                    value={metricsGenerales.viajesRealizados?.toLocaleString("es-AR") || "0"}
                    subtitle={`${metricsGenerales.viajesPendientes} pendientes`}
                    icon={FlightTakeoffIcon}
                    color="primary"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sistema de pestañas para navegación entre análisis */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="chart tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label="📍 Top Destinos" {...a11yProps(0)} />
                <Tab label="📅 Temporadas" {...a11yProps(1)} />
                <Tab label="🚌 Top Grupos" {...a11yProps(2)} />
                <Tab label="🏆 Vendedores" {...a11yProps(3)} />
                <Tab label="💰 Análisis de Pagos" {...a11yProps(4)} />
                {hasChannelData && <Tab label="📺 Canales" {...a11yProps(5)} />}
              </Tabs>
            </Box>

            {/* PESTAÑA 1: TOP DESTINOS MÁS VENDIDOS */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                📍 Top 10 Destinos Más Vendidos
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: ComposedChart con barras y línea */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Pasajeros vs Monto Total por Destino
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTopDestinos}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [formatCurrency(value), "Monto Total"]
                            if (name === "cantidadPasajeros") return [value, "Pasajeros"]
                            if (name === "nochesPromedio") return [value, "Noches Promedio"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="cantidadPasajeros" fill={COLORS[0]} name="Pasajeros" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="montoTotal"
                          stroke={COLORS[1]}
                          strokeWidth={3}
                          name="Monto Total"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top 5 destinos */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top 5 Destinos
                    </Typography>
                    <List>
                      {datosTopDestinos.slice(0, 5).map((destino, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {destino.nombre.length > 25 ? `${destino.nombre.substring(0, 25)}...` : destino.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                  {destino.cantidadPasajeros} pasajeros
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {destino.gruposVendidos} grupos
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {formatCurrency(destino.montoTotal)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 2: ANÁLISIS DE TEMPORADAS */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                📅 Análisis de Temporadas y Fechas de Viaje
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: Evolución temporal */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Evolución de Ventas por Mes de Viaje
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <AreaChart data={datosTemporadas}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="mes" angle={-45} textAnchor="end" height={100} interval={0} fontSize={11} />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [formatCurrency(value), "Monto Total"]
                            if (name === "pasajeros") return [value, "Pasajeros"]
                            if (name === "cantidadViajes") return [value, "Viajes"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="montoTotal"
                          stroke={COLORS[1]}
                          fill={COLORS[1]}
                          fillOpacity={0.6}
                          name="Monto Total"
                        />
                        <Area
                          type="monotone"
                          dataKey="pasajeros"
                          stroke={COLORS[0]}
                          fill={COLORS[0]}
                          fillOpacity={0.4}
                          name="Pasajeros"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico de barras: Pasajeros por temporada */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Pasajeros por Temporada
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={datosTemporadas}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="mes" angle={-45} textAnchor="end" height={100} interval={0} fontSize={10} />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => [value, "Pasajeros"]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="pasajeros" fill={COLORS[0]} name="Pasajeros" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Card: Mes con más viajes */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Temporada Alta
                    </Typography>
                    {datosTemporadas.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {(() => {
                          const mesConMasViajes = datosTemporadas.reduce((max, mes) => 
                            mes.cantidadViajes > max.cantidadViajes ? mes : max
                          , datosTemporadas[0])
                          return (
                            <Box>
                              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700, mb: 1 }}>
                                {mesConMasViajes.mes}
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                {mesConMasViajes.cantidadViajes} viajes realizados
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                {mesConMasViajes.pasajeros} pasajeros
                              </Typography>
                              <Typography variant="body1" color="success.main" sx={{ fontWeight: 600, mt: 1 }}>
                                {formatCurrency(mesConMasViajes.montoTotal)}
                              </Typography>
                            </Box>
                          )
                        })()}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 3: TOP GRUPOS/PAQUETES */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              🚌 Top Grupos y Paquetes Más Vendidos
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico de barras: Pasajeros por grupo */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Pasajeros por Grupo
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={datosTopGrupos}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={10} />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "cantidadPasajeros") return [value, "Pasajeros"]
                            if (name === "montoTotal") return [formatCurrency(value), "Monto Total"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar dataKey="cantidadPasajeros" fill={COLORS[4]} name="Pasajeros" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top 5 grupos */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top 5 Grupos
                    </Typography>
                    <List>
                      {datosTopGrupos.slice(0, 5).map((grupo, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index + 4], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {grupo.nombre.length > 25 ? `${grupo.nombre.substring(0, 25)}...` : grupo.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                  {grupo.cantidadPasajeros} pasajeros
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {grupo.destinosVisitados} destinos
                                </Typography>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(grupo.montoTotal)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>

                {/* Gráfico circular: Distribución de ventas por grupo */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Distribución de Ventas por Grupo
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={datosTopGrupos}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ nombre, montoTotal, percent }) => {
                            const nombreCorto = nombre.length > 20 ? nombre.substring(0, 20) + '...' : nombre
                            return `${nombreCorto}: ${(percent * 100).toFixed(1)}%`
                          }}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="montoTotal"
                        >
                          {datosTopGrupos.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), "Monto Total"]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 4: PERFORMANCE DE VENDEDORES */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                🏆 Performance de Vendedores
              </Typography>

              {/* Gráfico de área: Evolución temporal */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Evolución de Ventas por Día
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={datosAnalisisTemporal}>
                    <defs>
                      <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[1]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[1]} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorTransacciones" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" height={80} fontSize={11} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "totalVentas") return [formatCurrency(value), "Total Ventas"]
                        if (name === "cantidadTransacciones") return [value, "Transacciones"]
                        if (name === "ticketPromedio") return [formatCurrency(value), "Ticket Promedio"]
                        return [value, name]
                      }}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="totalVentas"
                      stroke={COLORS[1]}
                      fillOpacity={1}
                      fill="url(#colorVentas)"
                      name="Total Ventas"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cantidadTransacciones"
                      stroke={COLORS[0]}
                      strokeWidth={3}
                      name="Transacciones"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              {/* Métricas de resumen temporal */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Día con Mayor Venta"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? datosAnalisisTemporal.reduce((max, dia) => (dia.totalVentas > max.totalVentas ? dia : max))
                            .fecha
                        : "N/A"
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? formatCurrency(
                            datosAnalisisTemporal.reduce((max, dia) => (dia.totalVentas > max.totalVentas ? dia : max))
                              .totalVentas,
                          )
                        : "N/A"
                    }
                    icon={TrendingUpIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Promedio Diario"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? formatCurrency(
                            datosAnalisisTemporal.reduce((sum, dia) => sum + dia.totalVentas, 0) /
                              datosAnalisisTemporal.length,
                          )
                        : formatCurrency(0)
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? `${(datosAnalisisTemporal.reduce((sum, dia) => sum + dia.cantidadTransacciones, 0) / datosAnalisisTemporal.length).toFixed(1)} transacciones/día`
                        : "N/A"
                    }
                    icon={AnalyticsIcon}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Días Activos"
                    value={datosAnalisisTemporal.length}
                    subtitle="Período analizado"
                    icon={SellIcon}
                    color="warning"
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 5: ANÁLISIS DE PAGOS */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                💰 Análisis de Pagos y Cobranzas
              </Typography>

              <Grid container spacing={3}>
                {/* Cards de métricas financieras */}
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Total Pagado"
                    value={formatCurrency(datosAnalisisPagos.totalPagado || 0)}
                    subtitle="Cobranzas efectivas"
                    icon={AttachMoneyIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Saldo Pendiente"
                    value={formatCurrency(datosAnalisisPagos.totalSaldo || 0)}
                    subtitle="Por cobrar"
                    icon={AttachMoneyIcon}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Cobertura"
                    value={`${datosAnalisisPagos.promedioCobertura}%`}
                    subtitle="Porcentaje cobrado"
                    icon={AnalyticsIcon}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MetricCard
                    title="Pagos Completos"
                    value={datosAnalisisPagos.pasajerosSinDeuda}
                    subtitle={`${datosAnalisisPagos.pasajerosConDeuda} con saldo`}
                    icon={PersonIcon}
                    color="primary"
                  />
                </Grid>

                {/* Tabla Comparativa: Pagado vs Por Cobrar */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                      📊 Resumen Comparativo de Cobranzas
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          border: `2px solid ${theme.palette.success.main}`
                        }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            💰 Total Pagado
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {formatCurrency(datosAnalisisPagos.totalPagado || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Monto cobrado efectivamente
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          border: `2px solid ${theme.palette.warning.main}`
                        }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            ⏳ Total Por Cobrar
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                            {formatCurrency(datosAnalisisPagos.totalSaldo || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Saldo pendiente de cobro
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          border: `2px solid ${theme.palette.primary.main}`
                        }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            💵 Total General
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {formatCurrency((datosAnalisisPagos.totalPagado || 0) + (datosAnalisisPagos.totalSaldo || 0))}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Suma de pagado + por cobrar
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Lista de Paquetes Completamente Pagados */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2, maxHeight: 450, overflow: 'auto' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
                      ✅ Paquetes Pagados Completamente ({datosAnalisisPagos.pasajerosSinDeuda})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Viajes sin saldo pendiente
                    </Typography>
                    <List dense>
                      {data
                        .filter(item => (item.saldo || 0) === 0 && !item.liberado)
                        .slice(0, 20)
                        .map((item, index) => (
                          <ListItem 
                            key={index}
                            sx={{ 
                              borderBottom: '1px solid #f0f0f0',
                              '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.05) }
                            }}
                          >
                            <ListItemIcon>
                              <Avatar sx={{ bgcolor: 'success.main', width: 28, height: 28, fontSize: '0.75rem' }}>
                                ✓
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.item?.descripcion || 'Paquete'}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Cliente: {item.Transaccion?.entidad?.descripcion || 'N/A'}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    Pagado: {formatCurrency(item.pagado || 0)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      {data.filter(item => (item.saldo || 0) === 0 && !item.liberado).length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No hay paquetes completamente pagados en este filtro
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Paper>
                </Grid>

                {/* Gráfico circular: Pagado vs Saldo */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Estado de Pagos
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Pagado', value: datosAnalisisPagos.totalPagado || 0 },
                            { name: 'Saldo Pendiente', value: datosAnalisisPagos.totalSaldo || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          <Cell fill={COLORS[1]} />
                          <Cell fill={COLORS[2]} />
                        </Pie>
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), ""]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico de barras: Pasajeros por estado de pago */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Pasajeros por Estado de Pago
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={[
                          { estado: 'Pago Completo', cantidad: datosAnalisisPagos.pasajerosSinDeuda || 0 },
                          { estado: 'Con Saldo', cantidad: datosAnalisisPagos.pasajerosConDeuda || 0 }
                        ]}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="estado" />
                        <YAxis />
                        <Tooltip
                          formatter={(value) => [value, "Pasajeros"]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="cantidad" fill={COLORS[0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Lista de Paquetes con Saldo Pendiente */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2, maxHeight: 450, overflow: 'auto' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'warning.main' }}>
                      ⏳ Paquetes con Saldo Pendiente ({datosAnalisisPagos.pasajerosConDeuda})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Top 20 con mayor saldo por cobrar
                    </Typography>
                    <List dense>
                      {data
                        .filter(item => (item.saldo || 0) > 0)
                        .sort((a, b) => (b.saldo || 0) - (a.saldo || 0))
                        .slice(0, 20)
                        .map((item, index) => (
                          <ListItem 
                            key={index}
                            sx={{ 
                              borderBottom: '1px solid #f0f0f0',
                              '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.05) }
                            }}
                          >
                            <ListItemIcon>
                              <Avatar sx={{ bgcolor: 'warning.main', width: 28, height: 28, fontSize: '0.75rem' }}>
                                {index + 1}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.item?.descripcion || 'Paquete'}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Cliente: {item.Transaccion?.entidad?.descripcion || 'N/A'}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    Pagado: {formatCurrency(item.pagado || 0)}
                                  </Typography>
                                  {' • '}
                                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                    Saldo: {formatCurrency(item.saldo || 0)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      {data.filter(item => (item.saldo || 0) > 0).length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            ¡Excelente! No hay saldos pendientes
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {hasChannelData && (
              <TabPanel value={tabValue} index={5}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                  📺 Análisis de Canales de Venta
                </Typography>

                <Grid container spacing={3}>
                  {/* Performance Chart */}
                  <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Performance por Canal
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={channelData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={11} />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === "totalSales") return [`$${value.toLocaleString()}`, "Ventas Totales"]
                              if (name === "transactionCount") return [value, "Transacciones"]
                              return [value, name]
                            }}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Legend />
                          <Bar yAxisId="left" dataKey="totalSales" fill={COLORS[0]} name="Ventas Totales" />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="transactionCount"
                            stroke={COLORS[2]}
                            strokeWidth={3}
                            name="Transacciones"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Channel Metrics */}
                  <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Top 5 Canales
                      </Typography>
                      <List>
                        {channelData
                          .sort((a, b) => b.totalSales - a.totalSales)
                          .slice(0, 5)
                          .map((channel, index) => (
                            <ListItem key={channel.id} sx={{ px: 0 }}>
                              <ListItemIcon>
                                <Avatar
                                  sx={{ bgcolor: COLORS[index + 2], width: 32, height: 32, fontSize: "0.875rem" }}
                                >
                                  {index + 1}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {channel.name.length > 25 ? `${channel.name.substring(0, 25)}...` : channel.name}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                      ${channel.totalSales.toLocaleString()}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                      {channel.transactionCount} ventas • {channel.uniqueCustomers} clientes
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="primary.main">
                                      Ticket: ${channel.averageTicket.toLocaleString()}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Sales Distribution */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Distribución de Ventas
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={channelData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="totalSales"
                          >
                            {channelData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [`$${value.toLocaleString()}`, "Ventas"]}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Channel Efficiency */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Eficiencia por Canal
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart
                          data={channelData.sort((a, b) => b.averageTicket - a.averageTicket)}
                          layout="horizontal"
                          margin={{ left: 100 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, "Ticket Promedio"]} />
                          <Bar dataKey="averageTicket" fill={COLORS[1]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GraficasDetallesVentasModal
